<template>
  <div class="confirm-purchase">
    <transition
      :name="
        transitionDirection === DIRECTIONS.FORWARD
          ? 'fade-out-left'
          : 'fade-out-right'
      "
      mode="out-in"
    >
      <div v-if="isConfirming" class="mt-3">
        <placeholder-rect full-width height="200px" class="m-0" />
      </div>

      <template v-else-if="hasConfirmed">
        <template v-if="shouldUseIAP()">
          <confirm-purchase-iap
            :product-id="product.id"
            :email="purchaseEmail"
            @success="args => $emit('success', args)"
            @cancel="() => (hasConfirmed = false)"
            @error="handlePaymentError"
          />
        </template>
        <template v-else>
          <div class="confirm-purchase__payment-element">
            <payment-element
              :product-id="product.id"
              :is-subscription="isSubscription"
              :discount-info="discountInfo"
              :has-trial="hasTrial"
              :amount="amountInfo.amount"
              :amount-cents="amountInfo.amountCents"
              :currency="amountInfo.currency"
              :symbol="amountInfo.symbol"
              :is-loading-info="isLoadingTax || isApplyingCoupon"
              :email="purchaseEmail"
              @error="handlePaymentError"
              @change="handleUserDetailsChange"
              @back="() => (hasConfirmed = false)"
            />
          </div>
        </template>
      </template>

      <order-summary
        v-else
        v-model="purchaseEmail"
        :product="product"
        :has-trial="hasTrial"
        @change="handleEmailChange"
        @input="debounce(saveLastEmail, 250)"
      >
        <template v-if="!shouldUseIAP()">
          <template v-if="discountInfo">
            <list-item>
              <template slot="left">
                <div class="order-summary__left">
                  <span>{{
                    $t("components.checkout.confirmPurchase.discount")
                  }}</span>
                </div>
              </template>
              <div class="d-flex align-items-center w-100">
                <div>
                  <div class="v-list-item__title pl-2">
                    -{{ discountInfo.symbol }}{{ discountInfo.amount }}
                    {{ product.currency }}
                  </div>
                </div>
                <v-button
                  class="ml-auto flex-shrink-0"
                  primary
                  small
                  ghost
                  @click="attemptRemoveCoupon"
                >
                  {{ $t("components.checkout.confirmPurchase.remove") }}
                </v-button>
              </div>
            </list-item>

            <tax-info
              v-if="showTaxInfo"
              :tax-info="taxInfo"
              :no-coupon-tax-info="noCouponTaxInfo"
              :is-loading="isLoadingTax"
            />

            <list-item>
              <template slot="left">
                <div class="order-summary__left">
                  <span>
                    {{ $t("components.checkout.confirmPurchase.totalDue") }}
                  </span>
                </div>
              </template>
              <div class="v-list-item__title pl-2">
                <template v-if="showTaxInfo && taxInfo">
                  {{ taxInfo.symbol }}{{ taxInfo.final_amount }}
                  {{ taxInfo.currency }}
                  <template v-if="product.interval">
                    <template
                      v-if="discountInfo.duration === COUPON_DURATIONS.FOREVER"
                    >
                      {{ $t("components.checkout.confirmPurchase.orderPer") }}
                      {{ productInterval(product.interval) }}
                    </template>
                    <template
                      v-else-if="
                        discountInfo.duration === COUPON_DURATIONS.REPEATING &&
                          noCouponTaxInfo
                      "
                    >
                      {{ $t("components.checkout.confirmPurchase.orderPer") }}
                      {{ productInterval(product.interval) }}
                      for {{ discountInfo.duration_in_months }} months
                      <small class="d-block text-grey-60">
                        {{ $t("components.checkout.confirmPurchase.then") }}
                        {{ noCouponTaxInfo.symbol
                        }}{{ noCouponTaxInfo.final_amount }}
                        {{ noCouponTaxInfo.currency }}
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                      </small>
                    </template>
                    <template v-else-if="noCouponTaxInfo">
                      {{ $t("components.checkout.confirmPurchase.first") }}
                      {{ productInterval(product.interval) }}
                      <small class="d-block text-grey-60">
                        {{ $t("components.checkout.confirmPurchase.then") }}
                        {{ noCouponTaxInfo.symbol
                        }}{{ noCouponTaxInfo.final_amount }}
                        {{ noCouponTaxInfo.currency }}
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                      </small>
                    </template>
                  </template>
                </template>

                <template v-else>
                  {{ discountInfo.symbol }}{{ discountInfo.final_amount }}
                  {{ product.currency }}
                  <template v-if="product.interval">
                    <template
                      v-if="discountInfo.duration === COUPON_DURATIONS.FOREVER"
                    >
                      {{ $t("components.checkout.confirmPurchase.orderPer") }}
                      {{ productInterval(product.interval) }}
                    </template>
                    <template
                      v-else-if="
                        discountInfo.duration === COUPON_DURATIONS.REPEATING
                      "
                    >
                      {{ $t("components.checkout.confirmPurchase.orderPer") }}
                      {{ productInterval(product.interval) }}
                      for {{ discountInfo.duration_in_months }} months
                      <small class="d-block text-grey-60">
                        {{ $t("components.checkout.confirmPurchase.then") }}
                        {{ product.symbol }}{{ product.price }}
                        {{ product.currency }}
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                      </small>
                    </template>
                    <template v-else>
                      {{ $t("components.checkout.confirmPurchase.first") }}
                      {{ productInterval(product.interval) }}
                      <small class="d-block text-grey-60">
                        {{ $t("components.checkout.confirmPurchase.then") }}
                        {{ product.symbol }}{{ product.price }}
                        {{ product.currency }}
                        {{ $t("components.checkout.confirmPurchase.orderPer") }}
                        {{ productInterval(product.interval) }}
                      </small>
                    </template>
                  </template>
                </template>
              </div>
            </list-item>
          </template>

          <template v-else>
            <list-item
              v-if="!isElementHidden(HIDDEN_ELEMENTS.COUPON)"
              no-padding-top
              no-padding-right
            >
              <template slot="left">
                <div class="order-summary__left">
                  <span class="pt-1 mt-1">
                    {{ $t("components.checkout.confirmPurchase.coupon") }}
                  </span>
                </div>
              </template>

              <div
                v-if="!isPresent(discountInfo)"
                class="v-list-item__title pt-2 px-1 confirm-purchase__coupon-wrapper"
              >
                <template v-if="showCouponForm">
                  <div class="d-flex align-items-center">
                    <v-input
                      v-model="$v.couponCode.$model"
                      data-cy="coupon-input"
                      class="flex-grow-1"
                      validate-on-init
                      autofocus
                      :invalid-message="couponInvalidMessage"
                      :placeholder="
                        $t(
                          'components.checkout.confirmPurchase.placeholderCouponForm'
                        )
                      "
                      :validator="$v.couponCode"
                    />
                    <v-button
                      class="ml-auto"
                      round
                      ghost
                      @click="() => (showCouponForm = false)"
                    >
                      <v-icon icon="close"></v-icon>
                    </v-button>
                  </div>

                  <v-button
                    primary
                    small
                    block
                    data-role="apply-coupon"
                    data-cy="apply-coupon"
                    :disabled="!couponCode"
                    :is-loading="isApplyingCoupon"
                    class="mt-1"
                    @click="applyCouponAsync"
                  >
                    {{ $t("components.checkout.confirmPurchase.apply") }}
                  </v-button>
                </template>
                <template v-else>
                  <v-button
                    key="add-coupon-button"
                    class="d-flex align-items-center add-coupon-button p-0"
                    primary
                    small
                    ghost
                    @click="showCouponForm = true"
                  >
                    <v-icon icon="sell" />{{
                      $t("components.checkout.confirmPurchase.applyCoupon")
                    }}
                  </v-button>
                </template>
              </div>
            </list-item>

            <tax-info
              v-if="showTaxInfo"
              :tax-info="taxInfo"
              :is-loading="isLoadingTax"
            />
          </template>
        </template>

        <template v-slot:after>
          <div class="d-flex align-items-center mt-2">
            <v-button
              v-if="$listeners.back"
              class="mr-1"
              secondary
              round
              @click="() => $emit('back')"
            >
              <v-icon icon="arrow_back" />
            </v-button>
            <v-button
              primary
              block
              :disabled="!canContinue"
              :is-loading="isLoadingTax"
              @click="handleConfirm"
            >
              {{
                hasTrial
                  ? $t("components.checkout.confirmPurchase.continueFreeTrial")
                  : $t("components.checkout.confirmPurchase.continuePayment")
              }}
            </v-button>
          </div>
        </template>
      </order-summary>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import { validationMixin } from "vuelidate"

import { ACTIVITY_TYPES } from "@/store/analytics"

import debounce from "@/lib/debounce"
import { isPresent } from "@/lib/utils"
import { toDate, add } from "@/lib/date-utils"
import { productInterval } from "@/lib/product-interval"
import { shouldUseIAP } from "@/lib/checkout-utils"
import { HIDDEN_ELEMENTS } from "@/lib/constants"

import * as LocalStorage from "@/lib/local-storage"

import ListItem from "@/components/ListItem"
import vInput from "@/components/forms/vInput"
import PaymentElement from "@/components/checkout/PaymentElement"
import PlaceholderRect from "@/components/PlaceholderRect"
import OrderSummary from "@/components/checkout/OrderSummary"

export const COUPON_DURATIONS = {
  ONCE: "once",
  FOREVER: "forever",
  REPEATING: "repeating"
}

const DIRECTIONS = {
  FORWARD: "forward",
  BACKWARD: "backward"
}

export default {
  components: {
    ListItem,
    vInput,
    PaymentElement,
    PlaceholderRect,
    OrderSummary,
    TaxInfo: () => import("@/components/checkout/TaxInfo"),
    ConfirmPurchaseIap: () => import("@/components/checkout/ConfirmPurchaseIap")
  },

  props: {
    product: { type: Object, required: true },
    hasTrial: { type: Boolean, default: false }
  },

  data() {
    return {
      purchaseEmail: "",
      purchaseEmailValid: false,

      couponCode: "",
      discountInfo: null,
      showCouponForm: false,
      isApplyingCoupon: false,
      couponInvalidMessage: "",

      isConfirming: false,
      hasConfirmed: false,
      taxInfo: null,
      noCouponTaxInfo: null,
      isLoadingTax: false,

      transitionDirection: DIRECTIONS.FORWARD,

      COUPON_DURATIONS,
      DIRECTIONS,
      HIDDEN_ELEMENTS
    }
  },

  mixins: [validationMixin],

  mounted() {
    this.calculateTax()

    this.purchaseEmail = isPresent(this.currentUser)
      ? this.currentUser.email
      : LocalStorage.get(LocalStorage.LS_KEYS.LAST_EMAIL)
  },

  methods: {
    isPresent,
    toDate,
    add,
    productInterval,
    shouldUseIAP,
    debounce,

    ...mapActions("products", [
      "applyCoupon",
      "calculateTaxByIp",
      "fetchTaxByAddress",
      "fetchIntent"
    ]),
    ...mapActions("analytics", ["trackActivity"]),

    saveLastEmail() {
      LocalStorage.set(LocalStorage.LS_KEYS.LAST_EMAIL, this.purchaseEmail)
    },

    handlePaymentError(error) {
      this.$emit("error", error)
    },

    handleEmailChange({ valid }) {
      this.purchaseEmailValid = valid
    },

    handleUserDetailsChange({ name, address }) {
      if (this.hasEnabledTaxCollection && !this.discountZeroedPrice) {
        this.isLoadingTax = true
        this.taxInfo = null

        this.fetchTaxByAddress({
          productId: this.product.id,
          couponCode: this.couponCode,
          email: this.purchaseEmail,
          name,
          address
        })
          .then(({ tax }) => {
            this.taxInfo = tax

            this.isLoadingTax = false
          })
          .catch(err => {
            if (err.response?.data?.message) {
              this.$notify(err.response.data.message)
            } else {
              throw err
            }
          })
      }
    },

    calculateTax() {
      if (this.hasEnabledTaxCollection) {
        this.isLoadingTax = true
        this.taxInfo = null

        return this.calculateTaxByIp({
          productId: this.product.id,
          couponCode: this.couponCode
        })
          .then(({ tax, noCouponTax }) => {
            this.taxInfo = tax
            this.noCouponTaxInfo = noCouponTax
          })
          .catch(err => {
            if (err.response?.data?.message) {
              if (
                err.response.data.reason === "customer_tax_location_invalid"
              ) {
                this.taxInfo = null
                this.noCouponTaxInfo = null
                this.isLoadingTax = false
                // allow the user to enter their address in the next step
              }
            } else {
              throw err
            }
          })
          .finally(() => {
            this.isLoadingTax = false
          })
      }

      return Promise.resolve()
    },

    removeTax() {
      this.taxInfo = null
      this.noCouponTaxInfo = null
    },

    applyCouponAsync() {
      this.isApplyingCoupon = true
      this.applyCoupon({
        productId: this.product.id,
        couponCode: this.couponCode
      })
        .then(res => {
          this.discountInfo = res.data.discount_info

          if (this.hasEnabledTaxCollection) {
            if (this.discountZeroedPrice) {
              if (
                this.isSubscription &&
                this.discountInfo.duration === "once"
              ) {
                this.calculateTax()
              } else {
                this.removeTax()
              }
            } else {
              this.calculateTax()
            }
          }
        })
        .catch(err => {
          let message = "Coupon is not valid"
          if (err.response && err.response.data) {
            message = err.response.data.message
          }

          this.couponInvalidMessage = message
          this.discountInfo = null
        })
        .finally(() => (this.isApplyingCoupon = false))
    },

    attemptRemoveCoupon() {
      this.couponCode = ""
      this.discountInfo = null
      this.showCouponForm = false
      this.couponInvalidMessage = ""

      this.calculateTax()
    },

    handleConfirm() {
      this.transitionDirection = DIRECTIONS.FORWARD

      this.trackActivity({
        activityType: ACTIVITY_TYPES.CHECKOUT_PROGRESSED,
        data: {
          product: this.product,
          guest: this.isGuest,
          iap: shouldUseIAP()
        }
      })

      this.hasConfirmed = true
    },

    handleBack() {
      this.hasConfirmed = false
      this.$emit("back")
    }
  },

  watch: {
    couponCode(couponCode) {
      if (!couponCode) {
        this.couponInvalidMessage = ""
      }
    }
  },

  computed: {
    ...mapGetters(["hasEnabledTaxCollection", "shouldShowTax"]),
    ...mapGetters("auth", ["currentUser", "isGuest"]),
    ...mapGetters("ui", ["isElementHidden"]),

    canContinue() {
      return (
        !this.isLoadingTax && !this.isApplyingCoupon && this.purchaseEmailValid
      )
    },

    amountInfo() {
      if (isPresent(this.taxInfo)) {
        return {
          amount: this.taxInfo.final_amount,
          amountCents: this.taxInfo.final_amount_cents,
          currency: this.taxInfo.currency,
          symbol: this.taxInfo.symbol
        }
      } else if (isPresent(this.discountInfo)) {
        return {
          amount: this.discountInfo.final_amount,
          amountCents: this.discountInfo.final_amount_cents,
          currency: this.product.currency,
          symbol: this.product.symbol
        }
      } else {
        return {
          amount: this.formattedPrice,
          amountCents: this.product.price_cents,
          currency: this.product.currency,
          symbol: this.product.symbol
        }
      }
    },

    showTaxInfo() {
      return this.hasEnabledTaxCollection && this.shouldShowTax
    },

    isSubscription() {
      return this.product.purchase_type === "recurring"
    },

    formattedPrice() {
      return String(this.product.price_cents).endsWith("00")
        ? this.product.price_dollars
        : this.product.price
    },

    discountZeroedPrice() {
      return this.discountInfo?.final_amount_cents === 0
    }
  },

  validations: {
    couponCode: {}
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.confirm-purchase__tax-loading {
  .v-loader {
    width: $size--5;
    height: $size--5;
  }
}

.confirm-purchase__coupon-wrapper {
  .v-icon {
    position: relative;
    top: -1px;
    font-size: $font-size--small;
    color: var(--primary-brand-color-hex);
  }
}
</style>
